import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HiOutlineMenu } from 'react-icons/hi';
import { useAuth } from '../../context/AuthContext';
import { hasCardAccess } from '../../utils/permissionHelper';
import axiosInstance from '../../api/axiosConfig';
import { MODULES } from '../../constants/modules';
import Sidebar from '../Home/Sidebar';
import { useDeliveryRealtime } from '../../realtime/useDeliveryRealtime';
import DeliveryAlertToast from '../../realtime/DeliveryAlertToast';
import { useFrozenPickRealtime } from '../../realtime/useFrozenPickRealtime';
import FrozenPickAlertToast from '../../realtime/FrozenPickAlertToast';
import { useAnnouncements } from '../../realtime/useAnnouncements';
import AnnouncementModal from '../../realtime/AnnouncementModal';
import AnnouncementAdminDock from '../../realtime/AnnouncementAdminDock';
import { useAdminBubbles } from '../../realtime/useAdminBubbles';
import AdminBubbleToast from '../../realtime/AdminBubbleToast';
import { onSocketReconnect, onAppVisible } from '../../realtime/socketClient';
import { useAppSetupStatus } from '../../utils/appSetup';
import './AppShell.css';

/**
 * The application shell: left navigation + the current page.
 *
 * Mounted by the `ProtectedShell` layout route, so the navigation is present on
 * every authenticated page rather than only on Home — a user in a module moves
 * straight to the next one instead of going back first. Being a layout route it
 * is mounted once and survives navigation, so switching pages does not re-fetch
 * the nav or make it flicker.
 *
 * It also owns the two things the nav needs and pages happen to want as well:
 * the user's permissions and the pending counts. They live here rather than in
 * each page so there is one copy of each number in the app — see useAppShell().
 */

const AppShellContext = createContext(null);

/**
 * Read the shell's shared state from any page inside it.
 * Returns null when the page is rendered outside the shell (public routes).
 */
export const useAppShell = () => useContext(AppShellContext);

/**
 * Where each badge count comes from. One entry per card that has something
 * countable; a card missing here simply has no badge.
 *
 * `pick` is given `response.data`, so a shape change is a one-line fix here
 * instead of in seven near-identical fetch functions.
 */
const COUNT_SOURCES = {
    'visitor-entries': {
        url: '/api/forms?status=checked-in',
        pick: (d) => d?.forms?.length || 0
    },
    'task-management': {
        url: '/api/tasks/my-tasks?status=todo,in-progress&limit=1',
        pick: (d) => d?.totalTasks || 0
    },
    'monthly-documents': {
        url: '/api/monthly-docs/pending-verification',
        pick: (d) => d?.pendingCount || 0
    },
    'cheque-register': {
        url: '/api/cheques?status=pending&limit=1',
        pick: (d) => d?.total || 0
    },
    'cashbook': {
        url: '/api/cashbook?status=pending&limit=1',
        pick: (d) => d?.total || 0
    },
    'document-log-book': {
        url: '/api/document-log-book/pending/my?limit=1',
        pick: (d) => (d?.success ? (d.total || 0) : 0)
    },
    'clearance-form': {
        url: '/api/clearance/pending/my/count',
        pick: (d) => (d?.success ? (d.total || 0) : 0)
    },
    // Reuses the SAME withCounts=1 aggregation MyOrders' own status tabs
    // already ask for (deliveryController.js's getAllDeliveries) — no new
    // endpoint. For a delivery_boy this is auto-scoped server-side to their
    // own deliveries; for anyone else holding the card it's their branch's
    // open queue, same as every other badge here.
    'delivery-management': {
        url: '/api/deliveries?limit=1&withCounts=1',
        pick: (d) => (d?.statusCounts
            ? (d.statusCounts['Pending'] || 0) + (d.statusCounts['In Transit'] || 0)
            : 0)
    }
};

const AppShell = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();
    const [permissions, setPermissions] = useState(user?.permissions || { cards: [], actions: [] });
    const [counts, setCounts] = useState({});
    const [countLoading, setCountLoading] = useState({});
    // Open by default: on desktop (>=1024px) the CSS ignores this and the nav is
    // always visible; on mobile it starts as an open drawer instead of hidden.
    const [navOpen, setNavOpen] = useState(true);

    const isAdmin = user?.role === 'system_admin';

    // Live delivery alerts (WebSocket) + OS push subscribe — one connection
    // shared by the whole app, mounted here because AppShell is the one
    // component alive for the entire authenticated session. See
    // realtime/useDeliveryRealtime.js.
    const { alerts, dismissAlert } = useDeliveryRealtime();

    // Frozen-pick "new job" alerts — its own hook/toast pair (see
    // useFrozenPickRealtime.js header comment), reusing the socket connection
    // useDeliveryRealtime already opened rather than opening a second one.
    const { alerts: frozenPickAlerts, dismissAlert: dismissFrozenPickAlert } = useFrozenPickRealtime();

    // System-wide announcement popup — socket broadcast + web push, shown to
    // every logged-in user until its timer runs out. See realtime/useAnnouncements.js.
    const { announcement, dismiss: dismissAnnouncement } = useAnnouncements();

    // Transient corner "bubble" alerts for admins (new bug report / new reply).
    const { bubbles, dismiss: dismissBubble } = useAdminBubbles();

    // On a phone the drawer closes after the first tap, hiding the sidebar's
    // setup card — a dot on the menu button keeps the nag visible.
    const setup = useAppSetupStatus();

    useEffect(() => {
        if (!user) return;

        // Render immediately from the copy handed out at login...
        if (user.permissions) {
            setPermissions(user.permissions);
        }

        // ...then refresh from the server. That login copy is frozen until the
        // next sign-in, so without this a permission change (or a hidden home
        // tile) would not take effect until the user logged out.
        const refreshPermissions = async () => {
            try {
                const response = await axiosInstance.get('/api/permissions/my-permissions');
                if (response.data?.permissions) {
                    setPermissions(response.data.permissions);
                }
            } catch (error) {
                console.error('Error fetching permissions:', error);
            }
        };

        refreshPermissions();
    }, [user]);

    const cardsKey = (permissions.cards || []).join(',');

    const refreshCount = useCallback(async (cardId) => {
        const source = COUNT_SOURCES[cardId];
        if (!source) return;

        // Don't ask for a number the user is not allowed to see — the request
        // would 403 and log noise on every page load.
        if (!isAdmin && !hasCardAccess(permissions, cardId)) return;

        try {
            setCountLoading(prev => ({ ...prev, [cardId]: true }));
            const response = await axiosInstance.get(source.url);
            setCounts(prev => ({ ...prev, [cardId]: source.pick(response.data) }));
        } catch (error) {
            console.error(`Error fetching count for ${cardId}:`, error);
        } finally {
            setCountLoading(prev => ({ ...prev, [cardId]: false }));
        }
    }, [isAdmin, permissions]);

    useEffect(() => {
        if (!user) return;
        Object.keys(COUNT_SOURCES).forEach(cardId => refreshCount(cardId));
        // Re-runs when the user's cards change, not on every navigation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, cardsKey, isAdmin]);

    // The counts above previously only refreshed on login/permission change
    // and could go stale for the rest of the session. Two more discrete
    // triggers — a socket reconnect (was offline, came back) and the tab
    // regaining focus (was backgrounded, no reconnect needed because the
    // socket never dropped) — cover the gap without any interval polling.
    useEffect(() => {
        if (!user) return undefined;
        const refreshAll = () => Object.keys(COUNT_SOURCES).forEach(cardId => refreshCount(cardId));
        const offReconnect = onSocketReconnect(refreshAll);
        const offVisible = onAppVisible(refreshAll);
        return () => {
            offReconnect();
            offVisible();
        };
    }, [user, refreshCount]);

    // Modules this user may open. The nav renders from this, so it can never
    // offer a module the server would refuse.
    const visibleModules = useMemo(() => (
        MODULES.filter(module => isAdmin || hasCardAccess(permissions, module.id))
    ), [isAdmin, permissions]);

    // Which nav row is highlighted. Derived from the URL rather than passed in
    // by each page, so a page cannot forget to say where it is.
    const activeId = useMemo(() => {
        const path = location.pathname;
        if (path.startsWith('/purpose-selection')) return 'home';
        const match = MODULES.find(m => path === m.path || path.startsWith(`${m.path}/`));
        return match ? match.id : null;
    }, [location.pathname]);

    // Close the drawer after navigating on mobile — but not on the very first
    // render, which also matches this effect and would otherwise close the
    // drawer immediately after it opens by default.
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setNavOpen(false);
    }, [location.pathname]);

    // Stop the page behind the drawer from scrolling under the user's finger,
    // which on a phone reads as the app losing its place. Only below 1024px —
    // above that the nav isn't a drawer (see AppShell.css), so navOpen's
    // default `true` must not lock desktop scroll for the lifetime of the tab.
    useEffect(() => {
        if (!navOpen || window.innerWidth > 1024) return undefined;

        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [navOpen]);

    const contextValue = useMemo(() => ({
        permissions,
        counts,
        countLoading,
        refreshCount,
        isAdmin
    }), [permissions, counts, countLoading, refreshCount, isAdmin]);

    return (
        <AppShellContext.Provider value={contextValue}>
            <div className="app-shell">
                <Sidebar
                    modules={visibleModules}
                    badges={counts}
                    activeId={activeId}
                    isAdmin={isAdmin}
                    open={navOpen}
                    onClose={() => setNavOpen(false)}
                />

                <button
                    type="button"
                    className="app-shell-nav-toggle"
                    onClick={() => setNavOpen(true)}
                    aria-label="Open navigation"
                >
                    <HiOutlineMenu size={22} />
                    {!setup.complete && <span className="app-shell-nav-toggle-dot" />}
                </button>

                <div className="app-shell-main">
                    {children}
                </div>

                <DeliveryAlertToast alerts={alerts} onDismiss={dismissAlert} />
                <FrozenPickAlertToast alerts={frozenPickAlerts} onDismiss={dismissFrozenPickAlert} />
                <AnnouncementModal announcement={announcement} onDismiss={dismissAnnouncement} isAdmin={isAdmin} />
                {isAdmin && <AnnouncementAdminDock />}
                <AdminBubbleToast bubbles={bubbles} onDismiss={dismissBubble} />
            </div>
        </AppShellContext.Provider>
    );
};

export default AppShell;
